import React, { useEffect, useState } from 'react';
import { Button, Input, ToastMessage } from '@cokitchen/cokitchen-components';
import SelectDropdown from 'components/base/select-dropdown';
import { CategoryType, MealFormType, SectionType } from 'models/production-report.model';
import { defaultMealData } from '.';
import useProductionReportContext from 'contexts/kitchen/production-report.context/context';
import { useNavigate } from 'react-router-dom';
import { MAIN_STORE_ROUTE_KEYS, STORAGE_KEYS } from 'utils/constants';
import { toast } from 'react-toastify';
import { apiService } from 'services/base.service';
import AutoComplete from 'components/base/autocomplete';
type MealFormProps = {
  meal: MealFormType;
  mealCategories: CategoryType[];
  sections: SectionType[];
  index: number;
  setSelectedSectionId: (id: string) => void;
};

const MealForm = ({
  meal,
  mealCategories,
  sections,
  index,
  setSelectedSectionId
}: MealFormProps) => {
  const { mealFormData, setMealFormData, active } = useProductionReportContext();
  const [mealList, setMealList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mealName, setMealName] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const handleChange = (value: string, name: string, index: number) => {
    const updatedFormData = mealFormData.map((itm, i) => {
      if (index === i) {
        if (name === 'section') {
          return { ...itm, [name]: value, meal_category: '' };
        } else {
          return { ...itm, [name]: value };
        }
      } else {
        return itm;
      }
    });

    setMealFormData(updatedFormData);
  };
  const removeMeal = (index: number) => {
    setMealFormData(mealFormData.filter((meal, i) => index !== i));
  };

  const searchMealName = async (mealName: string) => {
    if (mealName.length < 4) return;
    try {
      setLoading(true);
      const res = await apiService.get(`cokitchen-item/kitchen-items?name=${mealName}`);
      const options =
        res.data?.results?.map((meal: any) => ({
          value: meal.name,
          text: meal.name
        })) ?? [];
      setMealList(options);
    } catch (error) {
      setMealList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (mealName && mealName !== meal.meal_name) {
      setShowOptions(true);
      searchMealName(mealName);
    }
  }, [mealName]);

  return (
    <div className='space-y-4 border-b border-[#E9EAEC] py-4'>
      <SelectDropdown
        label='Section'
        options={sections.slice(1)}
        placeholder='Select section'
        value={meal.section}
        setValue={(value) => {
          handleChange(value.name, 'section', index);
          setSelectedSectionId(value.id);
        }}
      />
      <SelectDropdown
        label='Meal category'
        options={mealCategories}
        placeholder='Type category name'
        value={meal.meal_category}
        setValue={(value) => handleChange(value.name, 'meal_category', index)}
      />
      {active === 'csv' ? (
        <Input
          design='bordered'
          name='meal_name'
          label='Meal name'
          value={meal.meal_name}
          placeholder='Carb rice & spices'
          onChange={(e) => handleChange(e.target.value, 'meal_name', index)}
        />
      ) : (
        <AutoComplete
          emptyText='No meal matches your search'
          label='Meal name'
          placeholder='Type meal name'
          loading={loading}
          value={mealName}
          options={mealList}
          onSearch={setMealName}
          onSelect={(val) => {
            handleChange(val.text, 'meal_name', index);
            setMealName(val.text);
            setShowOptions(false);
          }}
          showOptions={showOptions}
          setShowOptions={setShowOptions}
        />
      )}
      <div className='mb-10 relative'>
        <Input
          design='bordered'
          name='min_quantity'
          type='tel'
          pattern='[0-9]*'
          label='Min. qty'
          value={meal.min_quantity}
          placeholder='00'
          onChange={(e) => handleChange(e.target.value, 'min_quantity', index)}
        />
        <p className='text-sm text-[#96999C] absolute top-[55%] right-4 z-10'>Portion</p>
      </div>
      <div className='flex justify-end'>
        {index >= 1 && (
          <button onClick={() => removeMeal(index)} className='text-[#E60A2B] font-medium text-sm '>
            - Remove meal
          </button>
        )}
      </div>
    </div>
  );
};

const AddMealForm = () => {
  const { mealFormData, setMealFormData, sections, setActive } = useProductionReportContext();
  const [mealCategories, setMealCategories] = useState<CategoryType[]>([]);
  const [selectedSectionId, setSelectedSectionId] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const cokitchenId = localStorage.getItem(STORAGE_KEYS.COKITCHEN);

  const addMeal = () => {
    setMealFormData([...mealFormData, { ...defaultMealData }]);
  };

  const hasEmptyField = (data: MealFormType[]): boolean => {
    for (const item of data) {
      for (const key in item) {
        if (
          typeof item[key as keyof MealFormType] === 'string' &&
          item[key as keyof MealFormType] === ''
        ) {
          return true;
        }
      }
    }
    return false;
  };

  const fetchMealCategory = async () => {
    if (!selectedSectionId) return;
    try {
      const res = await apiService.get(
        `cokitchen-item/cokitchen/${cokitchenId}/sections/${selectedSectionId}/categories`
      );
      setMealCategories(res?.data);
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err.response.data.message} />);
    }
  };

  useEffect(() => {
    fetchMealCategory();
  }, [cokitchenId, selectedSectionId]);

  const createMeal = async () => {
    const data = mealFormData.map((item) => ({
      cokitchen_id: cokitchenId,
      kitchen_item_name: item.meal_name,
      category: item.meal_category,
      section: item.section,
      minimun_stock_level: Number(item.min_quantity)
    }));

    try {
      setLoading(true);
      const res = await apiService.post(`cokitchen-item/create-with-item-name`, {
        data: data[0]
      });
      if (res) {
        toast(<ToastMessage type='success' message='Meal added successfully' />);
        setMealFormData([]);
        setActive(null);
        navigate(MAIN_STORE_ROUTE_KEYS.KITCHEN_PRODUCTION_REPORT);
      }
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err.response.data.message} />);
    } finally {
      setLoading(false);
    }
  };

  const proceed = () => {
    if (mealFormData.length <= 1) {
      createMeal();
    } else {
      navigate(MAIN_STORE_ROUTE_KEYS.KITCHEN_REVIEW_MEAL);
    }
  };
  return (
    <div>
      {mealFormData.map((item, index) => (
        <MealForm
          key={index}
          meal={item}
          mealCategories={mealCategories}
          sections={sections}
          index={index}
          setSelectedSectionId={setSelectedSectionId}
        />
      ))}
      <button onClick={addMeal} className='text-[#0080E5] mt-2 font-medium text-sm'>
        + Add Item
      </button>
      <Button
        color='primary'
        variant='block'
        small
        className='mt-8'
        onClick={proceed}
        disabled={hasEmptyField(mealFormData)}
        loading={loading}>
        {mealFormData.length <= 1 ? 'Confirm' : 'Continue'}
      </Button>
    </div>
  );
};

export default AddMealForm;
